import { Injectable } from '@angular/core';
import { OperationService } from '../operation/operation.service';
import { DailyLogEntriesService } from '../../daily-log/daily-log-entries/daily-log-entries.service';
import { PtLuxonDate } from '../../../models/projects/date.models';
import {
  LocationOption,
  SetupChangeLogRecord,
  UpdatedSetupSummary
} from '../../../models/setup/setup.models';
import { DateParserService } from '../../daily-log/daily-log-time/date-parser-service';
import { DailyLogService } from '../../daily-log/daily-log.service';
import {
  DailyLogOperationInstance,
  DailyOperationLogRecord,
  DailyOperationLogRecordHelper,
  RequiredRecordHelper,
  StepInstance,
  TransitingHelper
} from '../../../models/projects/operation.models';
import { DateChangeRecordService } from '../../daily-log/date-change-record/date-change-record.service';
import { cloneDeep } from 'lodash';
import { SetupService } from '../../setup/setup.service';
import { TimeZoneService } from '../../time-zone/time-zone.service';
import { AppData, OperationInstanceSaveRequest } from '../../../models/app-data/app-data';
import { AppConfig } from '../../../app/app.config';
import { HttpClient } from '@angular/common/http';
import { DateTime } from 'luxon';

@Injectable()
export class OperationSaveService {
  config = AppConfig.settings;

  constructor(
    public operationService: OperationService,
    public dailyLogEntryService: DailyLogEntriesService,
    public dateParserService: DateParserService,
    public dailyLogService: DailyLogService,
    private dateChangeRecordService: DateChangeRecordService,
    private setupService: SetupService,
    private timeZoneService: TimeZoneService,
    private http: HttpClient
  ) {
  }

  async saveOperationInstance(saveRequest: OperationInstanceSaveRequest): Promise<DailyLogOperationInstance> {
    const url = `${this.config.apiUrl}api/operations/save`;

    saveRequest = await this.removeOffsetsFromSaveRequest(saveRequest);

    return this.http
      .post<DailyLogOperationInstance>(url, saveRequest)
      .toPromise()
      .then(async (data: DailyLogOperationInstance) => {
        // todo check if we need to deserialize dates here or not
        // note that we don't deserialize the dates here as it's not needed
        return data;
      });
  }

  async removeOffsetsFromSaveRequest(saveRequest: OperationInstanceSaveRequest): Promise<OperationInstanceSaveRequest> {
    saveRequest.operationInstanceEditor = this.dateParserService.removeOffsetFromLuxonDates(saveRequest.operationInstanceEditor);
    saveRequest.jackingActivityEditor = this.dateParserService.removeOffsetFromLuxonDates(saveRequest.jackingActivityEditor);
    saveRequest.voyageLog = this.dateParserService.removeOffsetFromLuxonDates(saveRequest.voyageLog);
    saveRequest.dailyLogDate = this.dateParserService.removeOffsetFromLuxonDate(saveRequest.dailyLogDate);

    return saveRequest;
  }

  async saveOperation(
    operationInstance: DailyLogOperationInstance,
    stepInstances: StepInstance[],
    oldLogs: DailyOperationLogRecord[],
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean, // used in getting the correct offsets in the dailyLogEntryService,
    useStepNumberComparison?: boolean
  ): Promise<DailyOperationLogRecord[]> {
    operationInstance.cancelledTime = await this.dateParserService.deserializeDate(operationInstance.cancelledTime);
    const requiredRecordsHelper = await this.calculateRequiredRecords(operationInstance);
    const recordCount = this.calculateRecordCount(operationInstance, requiredRecordsHelper);
    let id = await this.operationService.getDailyLogRecordTempId();
    let savedRecords: DailyOperationLogRecord[] = [];
    let uniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(recordCount);
    let helper: DailyOperationLogRecordHelper =
      cloneDeep(await this.dailyLogEntryService
        .createOperationStartLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));

    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    if (stepInstances != null) {
      helper = cloneDeep(await this.dailyLogEntryService
        .createOperationStepLogEntry(id, stepInstances, operationInstance, uniqueOrdinal, oldLogs, localOperationInstance, datesAmbiguous, useStepNumberComparison));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    helper = cloneDeep(await this.dailyLogEntryService
      .createOperationEndLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));
    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    if (operationInstance.comments != null && operationInstance.comments.length > 0) {
      helper = cloneDeep(await this.dailyLogEntryService.createOperationCommentLogEntry(id, operationInstance, uniqueOrdinal, oldLogs));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    if (!!operationInstance.cancelledTime) {
      operationInstance.cancelledTime = cloneDeep(operationInstance.cancelledTime);
    }

    if (requiredRecordsHelper.cancelRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createCancelEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    } else {
      await this.dailyLogEntryService.removeCancelEntry(oldLogs);
    }

    if (requiredRecordsHelper.switchTimeRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createSwitchTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    if (!operationInstance.newLocationId) {
      await this.dailyLogEntryService.updateLocationChangeTime(operationInstance.id, cloneDeep(operationInstance.cancelledTime));
    }

    operationInstance.pausedTimes.forEach(time => {
      time.startDate = PtLuxonDate.assignPtLuxonDate(time.startDate);
      time.endDate = PtLuxonDate.assignPtLuxonDate(time.endDate);
    });

    if (!!operationInstance.pausedTimes) {
      operationInstance.pausedTimes = operationInstance.pausedTimes
        .filter(p => p.startDate.dateTime > operationInstance.startTime.getDate());
    }

    if (requiredRecordsHelper.pauseRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createPauseTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    if (requiredRecordsHelper.resumeRecordsRequired) {
      helper = await this.dailyLogEntryService.createResumedTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous);
      savedRecords = savedRecords.concat(helper.records);
    }

    // await this.dailyLogEntryService.cleanseOperationPauseTimes(operationInstance);
    // await this.dailyLogEntryService.cleanseOperationResumeTimes(operationInstance);
    // await this.dailyLogEntryService.cleanseOperationSwitchTimes(operationInstance);

    return savedRecords;
  }

  async createTransitingHelper(
    operationInstance: DailyLogOperationInstance,
    stepInstances: StepInstance[],
    newLocationId: number,
    setupSummary: UpdatedSetupSummary,
    locationOptions: LocationOption[],
    oldLogs?: DailyOperationLogRecord[],
    transitingOrdinal?: number,
  ): Promise<TransitingHelper> {
    let savedRecords: DailyOperationLogRecord[] = [];
    let id = await this.operationService.getDailyLogRecordTempId();
    const previousLocation = await this.setupService.getLocationForTime(operationInstance.startTime.dateTime, true);
    const requiredRecordsHelper = await this.calculateRequiredRecords(operationInstance, previousLocation, newLocationId);

    let helper: DailyOperationLogRecordHelper;

    if (requiredRecordsHelper.transitingRecordRequired) {
      const locationName = locationOptions.find(e => e.id === newLocationId).name;
      // we dont care about unique ordinal as it's being overwritten on api
      helper = cloneDeep(await this.dailyLogEntryService
        .createTransitingToLogEntry(locationName, id, operationInstance, 1, oldLogs, transitingOrdinal));

      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
    }

    if (!!operationInstance.cancelledTime) {
      operationInstance.cancelledTime = cloneDeep(operationInstance.cancelledTime);
    }

    // todo are these required?
    // if (requiredRecordsHelper.cancelRecordsRequired) {
    //   helper = cloneDeep(await this.dailyLogEntryService.createCancelEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
    //   savedRecords = savedRecords.concat(helper.records);
    //   id = helper.nextId ?? id;
    // } else {
    //   await this.dailyLogEntryService.removeCancelEntry(oldLogs);
    // }
    //
    // if (requiredRecordsHelper.switchTimeRecordsRequired) {
    //   helper = cloneDeep(await this.dailyLogEntryService.createSwitchTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
    //   savedRecords = savedRecords.concat(helper.records);
    //   id = helper.nextId ?? id;
    // }

    // if (requiredRecordsHelper.pauseRecordsRequired) {
    //   helper = cloneDeep(await this.dailyLogEntryService.createPauseTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
    //   savedRecords = savedRecords.concat(helper.records);
    //   id = helper.nextId ?? id;
    // }
    //
    // if (requiredRecordsHelper.resumeRecordsRequired) {
    //   helper = cloneDeep(await this.dailyLogEntryService.createResumedTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
    //   savedRecords = savedRecords.concat(helper.records);
    //   id = helper.nextId ?? id;
    // }

    if (!operationInstance.newLocationId) {
      await this.dailyLogEntryService.updateLocationChangeTime(operationInstance.id, cloneDeep(operationInstance.cancelledTime));
    }

    return {id: id, records: savedRecords, finalUniqueOrdinal: null};
  }

  async saveTransitingOperation(
    operationInstance: DailyLogOperationInstance,
    stepInstances: StepInstance[],
    newLocationId: number,
    setupSummary: UpdatedSetupSummary,
    locationOptions: LocationOption[],
    oldLogs?: DailyOperationLogRecord[],
    transitingOrdinal?: number,
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean,
    additionalTransitingCount?: number // needed to ensure correct uniqueOrdinal is returned from theAPI
  ): Promise<TransitingHelper> {
    let savedRecords: DailyOperationLogRecord[] = [];
    let id = await this.operationService.getDailyLogRecordTempId();
    const previousLocation = await this.setupService.getLocationForTime(operationInstance.startTime.dateTime, true);
    const requiredRecordsHelper = await this.calculateRequiredRecords(operationInstance, previousLocation, newLocationId);
    const recordCount = this.calculateRecordCount(operationInstance, requiredRecordsHelper);
    let uniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(recordCount + (additionalTransitingCount || 0))

    let helper: DailyOperationLogRecordHelper;

    if (requiredRecordsHelper.transitingRecordRequired) {
      const locationName = locationOptions.find(e => e.id === newLocationId).name;
      helper = cloneDeep(await this.dailyLogEntryService
        .createTransitingToLogEntry(locationName, id, operationInstance, uniqueOrdinal, oldLogs, transitingOrdinal));

      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    helper = cloneDeep(await this.dailyLogEntryService
      .createOperationStartLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));
    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId ?? id;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    if (stepInstances != null) {
      helper = cloneDeep(await this.dailyLogEntryService
        .createOperationStepLogEntry(id, stepInstances, operationInstance, uniqueOrdinal, oldLogs, localOperationInstance, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    helper = cloneDeep(await this.dailyLogEntryService
      .createOperationEndLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));
    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId ?? id;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    if (operationInstance.comments != null && operationInstance.comments.length > 0) {
      helper = cloneDeep(await this.dailyLogEntryService.createOperationCommentLogEntry(id, operationInstance, uniqueOrdinal, oldLogs));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    if (!!operationInstance.cancelledTime) {
      operationInstance.cancelledTime = cloneDeep(operationInstance.cancelledTime);
    }

    if (requiredRecordsHelper.cancelRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createCancelEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
    } else {
      await this.dailyLogEntryService.removeCancelEntry(oldLogs);
    }

    if (requiredRecordsHelper.switchTimeRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createSwitchTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
    }

    if (requiredRecordsHelper.pauseRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createPauseTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
    }

    if (requiredRecordsHelper.resumeRecordsRequired) {
      helper = cloneDeep(await this.dailyLogEntryService.createResumedTimeEntries(id, operationInstance, uniqueOrdinal, oldLogs, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId ?? id;
    }

    if (!operationInstance.newLocationId) {
      await this.dailyLogEntryService.updateLocationChangeTime(operationInstance.id, cloneDeep(operationInstance.cancelledTime));
    }

    return {id: id, records: savedRecords, finalUniqueOrdinal: uniqueOrdinal};
  }

  async saveDelay(
    operationInstance: DailyLogOperationInstance,
    oldLogs: DailyOperationLogRecord[],
    localOperationInstance?: DailyLogOperationInstance,
    datesAmbiguous?: boolean
  ): Promise<DailyOperationLogRecord[]> {
    let savedRecords: DailyOperationLogRecord[] = [];

    let id = await this.operationService.getDailyLogRecordTempId();
    const requiredRecordsHelper = await this.calculateRequiredRecords(operationInstance);
    const recordCount = this.calculateRecordCount(operationInstance, requiredRecordsHelper);
    let uniqueOrdinal = await this.operationService.getDailyLogRecordUniqueOrdinal(recordCount);

    let helper: DailyOperationLogRecordHelper = cloneDeep(await this.dailyLogEntryService
      .createOperationStartLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));

    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    const steps = !operationInstance.stepCollectionInstance
      ? null
      : operationInstance.stepCollectionInstance.stepInstances;

    if (steps != null) {
      helper = cloneDeep(await this.dailyLogEntryService
        .createOperationStepLogEntry(id, steps, operationInstance, uniqueOrdinal, oldLogs, localOperationInstance, datesAmbiguous));
      savedRecords = savedRecords.concat(helper.records);
      id = helper.nextId;
      uniqueOrdinal = helper.nextUniqueOrdinal;
    }

    helper = cloneDeep(await this.dailyLogEntryService
      .createOperationEndLogEntry(id, operationInstance, uniqueOrdinal, oldLogs, null, localOperationInstance, datesAmbiguous));
    savedRecords = savedRecords.concat(helper.records);
    id = helper.nextId;
    uniqueOrdinal = helper.nextUniqueOrdinal;

    if (operationInstance.comments != null) {
      helper = cloneDeep(await this.dailyLogEntryService.createOperationCommentLogEntry(id, operationInstance, uniqueOrdinal, oldLogs));
      savedRecords = savedRecords.concat(helper.records);
    }

    return savedRecords;
  }

  private async calculateRequiredRecords(
    operationInstance: DailyLogOperationInstance,
    previousLocation?: SetupChangeLogRecord,
    newLocationId?: number
  )
    : Promise<RequiredRecordHelper> {
    const helper = new RequiredRecordHelper();

    helper.cancelRecordsRequired = !!operationInstance.cancelledTime && !isNaN(operationInstance.cancelledTime.toMillis());
    helper.switchTimeRecordsRequired = !!operationInstance.switchTimes && operationInstance.switchTimes.length > 0;
    helper.pauseRecordsRequired = !!operationInstance.pausedTimes && operationInstance.pausedTimes.length > 0;
    helper.resumeRecordsRequired = !!operationInstance.pausedTimes && operationInstance.pausedTimes.length > 0;

    if (previousLocation && newLocationId) {
      helper.transitingRecordRequired = previousLocation == null || newLocationId !== previousLocation.newValueId;
    }

    return helper;
  }

  /* Before getting the first unique ordinal, the api needs to know how many ordinals to allocate - based on how many records are going
  to be created */
  private calculateRecordCount(operationInstance: DailyLogOperationInstance, requiredRecordHelper: RequiredRecordHelper): number {
    let count = 2; // factoring in operation start + end

    if (operationInstance.stepCollectionInstance && operationInstance.stepCollectionInstance.stepInstances) {
      count = count + operationInstance.stepCollectionInstance.stepInstances.length;
    }

    if (operationInstance.comments) {
      count = count + operationInstance.comments.length;
    }

    if (requiredRecordHelper.cancelRecordsRequired) {
      count++;
    }

    if (requiredRecordHelper.pauseRecordsRequired) {
      count++;
    }

    if (requiredRecordHelper.resumeRecordsRequired) {
      count++;
    }

    if (requiredRecordHelper.switchTimeRecordsRequired) {
      count++;
    }

    if (requiredRecordHelper.transitingRecordRequired) {
      count++;
    }

    return count;
  }
}



// WEBPACK FOOTER //
// ./src/services/operations/operation-save/operation-save.service.ts